import { createFileRoute } from "@tanstack/react-router";
import { Inbox, Tag, Users } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/contacts-hero.webp";
import captureImg from "@/assets/contacts-capture.webp";
import segmentsImg from "@/assets/contacts-segments.webp";

const IMG: Record<string, string> = { hero: heroImg, capture: captureImg, segments: segmentsImg };

export const Route = createFileRoute("/el/platform/contacts")({
  head: () => ({
    meta: [
      { title: "CRM Επαφές thông minh — banana.cards" },
      { name: "description", content: "Αυτόματη συλλογή υποψήφιων πελατών, hồ sơ làm giàu, gắn thẻ thông minh và pipeline vòng đời — CRM làm elệc thay bạn." },
      { property: "og:title", content: "CRM Επαφές thông minh — banana.cards" },
      { property: "og:description", content: "Αυτόματη συλλογή υποψήφιων πελατών, hồ sơ làm giàu, gắn thẻ thông minh và pipeline vòng đời — CRM làm elệc thay bạn." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/platform/contacts", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="CRM Επαφές"
      title="Ένα CRM που συλλέγει υποψήφιους πελάτες"
      italic="ενώ κοιμάστε."
      description="Κάθε άγγιγμα, σάρωση και φόρμα καταγράφεται αυτόματα στο CRM. Έξυπνες ετικέτες, εμπλουτισμένα προφίλ και pipeline κύκλου ζωής — τίποτα δεν σας ξεφεύγει."
      heroImage={IMG['hero']}
      metrics={[{ v: "0", l: "χειροκίνητες καταχωρίσεις" }, { v: "100%", l: "καταγεγραμμένα" }, { v: "3x", l: "ταχύτερο follow-up" }]}
      bullets={[
        { icon: Inbox, title: "Αυτόματη συλλογή υποψήφιων πελατών", desc: "Κάθε κοινοποίηση, σάρωση, φόρμα και συμβάν καταλήγει σε ένα inbox." },
        { icon: Tag, title: "Έξυπνες ετικέτες", desc: "Το AI προτείνει ετικέτες βάσει συμβάντος, πηγής, ρόλου και πρόσφατης δραστηριότητας." },
        { icon: Users, title: "Εμπλουτισμένα προφίλ", desc: "Email, κοινωνικά δίκτυα, εταιρεία, τελευταία επαφή — όλα εμπλουτίζονται και αποδιπλοποιούνται." }
      ]}
      deepDives={[
        { eyebrow: "Thu thập", title: "Một inbox, mọi", italic: "nguồn lead.", description: "Chạm NFC, quét QR, gửi form, quét danh thiếp giấy và webhook đều vào một inbox đã phân loại.", image: IMG["capture"], reverse: false, points: ["Bộ lọc và thẻ theo nguồn", "Khử trùng lặp khi thu thập", "Quy tắc gán cho từng đội", "Tự lưu trữ sau khi theo dõi"] },
        { eyebrow: "Phân khúc", title: "Lọc, phân khúc,", italic: "kích hoạt.", description: "Xây phân khúc thông minh (ví dụ 'gặp tại SaaStr, không theo dõi trong 14 ngày') và kích hoạt tự động hóa hoặc chiến dịch từ đó.", image: IMG["segments"], reverse: true, points: ["Phân khúc thông minh với số đếm trực tiếp", "View lưu theo đội", "Hành động hàng loạt và εξαγωγή", "Đồng bộ tới công cụ chiến dịch ngoài"] }
      ]}
      howItWorks={[
        { title: "Thu thập mọi nơi", desc: "Chạm thẻ, quét, form, nhập." },
        { title: "Banana khử trùng lặp", desc: "Một liên hệ, mọi nguồn." },
        { title: "Gắn thẻ và gán", desc: "Quy tắc thông minh theo workspace." },
        { title: "Theo dõi nhanh", desc: "Nhắc, elệc cần làm và bản thảo AI." }
      ]}
      capabilities={[
        "Tự thu thập từ mọi nguồn",
        "Khử trùng lặp & gộp",
        "Trường tùy chỉnh và pipeline",
        "Phân khúc thông minh đã lưu",
        "Nhập / εξαγωγή hàng loạt",
        "Phân quyền theo đội",
        "Xuất CSV & API",
        "Webhook khi thay đổi"
      ]}
      faq={[
        { q: "Επαφές mới vào CRM bằng cách nào?", a: "Banana thu thập liên hệ từ mọi hướng mà bạn không cần gõ: mỗi lần chạm NFC và quét QR tạo một liên hệ, form Lead Exchange trên thẻ hỏi đối phương thông tin, AI quét danh thiếp giấy biến ảnh thẻ giấy thành liên hệ sạch, và bạn có thể nhập hàng loạt từ CSV, Google Contacts, Outlook hoặc CRM lớn nào đó." },
        { q: "Có thể quét danh thiếp giấy thành liên hệ không?", a: "Có. Chụp ảnh thẻ giấy trong app Banana và AI quét trích εξαγωγή tên, công ty, chức danh, email, điện thoại, website và địa chỉ thành liên hệ có cấu trúc — sau đó khử trùng lặp với danh sách hiện có. Tuyệt cho triển lãm và hội nghị." },
        { q: "Có thể gắn thẻ, phân khúc và lọc liên hệ không?", a: "Có. Tạo thẻ màu không giới hạn (ví dụ 'Lead nóng', 'Khách hàng', 'Nhà cung cấp', 'Đối tác'), gán nhiều thẻ cho mỗi liên hệ, và lọc danh sách theo tổ hợp thẻ + tìm kiếm tự do. Dùng sắp xếp và bộ lọc thẻ để xây phân khúc đã lưu cho outreach hoặc tự động hóa." },
        { q: "Đội tôi dùng chung một pool hay mỗi người riêng?", a: "Workspace chia sẻ một sổ liên hệ — mọi đồng nghiệp thấy cùng liên hệ và timeline, với phân quyền theo vai trò kiểm soát ai được sửa hoặc xóa. Ghi chú cá nhân có thể giữ riêng cho từng người." },
        { q: "Có nhập từ HubSpot, Salesforce hoặc CRM hiện tại không?", a: "Có — nhập CSV một cú nhấp hoạt động với định dạng εξαγωγή của mọi CRM lớn (HubSpot, Salesforce, Pipedrive, Zoho, Monday, Close, Folk). Ánh xạ trường tự động cho trường chuẩn và chỉnh được cho trường tùy chỉnh, với phát hiện trùng lặp theo email và điện thoại." },
        { q: "Có API để đẩy liên hệ từ hệ thống riêng không?", a: "Có — REST API đầy đủ cộng webhook bắn khi liên hệ được tạo/cập nhật, và Banana có tích hợp Zapier native với 6.000+ app nếu bạn muốn nối hình ảnh." }
      ]}
      outro={ { title: "Đừng đánh mất lead", italic: "lần nào nữa.", description: "Một inbox cho mọi kết nối — làm giàu, gắn thẻ và sẵn sàng hành động." } }
    />
  );
}